import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IError, JsonEditorComponent, JsonEditorOptions } from 'ang-jsoneditor';
import { Subscription } from 'rxjs';
import { UniqueNameValidation } from 'src/app/Modules/management/core/Validators/unique-name.validator';
import {
    ActivityIcon,
    ActivityPackStatus,
    ActivityPackType,
    ActivityStatusType,
    ActivityTagStandard,
} from 'src/app/Modules/management/models/instance/activity-pacck-type.model';
import { SchemaRevision, SchemaVersion } from 'src/app/Modules/management/models/instance/project-type.model';
import { CustomAvatarService } from 'src/app/services/custom-avatar.service';
import { CustomValidatorService } from 'src/app/services/custom-validator.service';
import { InstanceDataService } from 'src/app/services/instance-data.service';

@Component({
    selector: 'app-create-activity-pack-modal',
    templateUrl: './create-activity-pack-modal.component.html',
    styleUrls: ['./create-activity-pack-modal.component.scss'],
    standalone: false,
})
export class CreateActivityPackModalComponent implements OnInit {
    @Input() modalMode: string;
    @Input() activityPackTypeSchema: string;
    @Input() activityPackType: ActivityPackType;
    @Input() defaultActivityPackTypeList: ActivityPackType[];
    @Input() defaultActivityPackStatusList: ActivityPackStatus[];
    @Input() defaultActivityIconList: ActivityIcon[];
    @Input() activityPackNameList: string[];
    @Input() defaultActivityTagStandardList: ActivityTagStandard[];
    @ViewChild('editor') editor: JsonEditorComponent;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

    tooltip_position = 'above';
    editorOptions: JsonEditorOptions;
    schemaList: SchemaVersion[] = [];
    reverseSchemaList: SchemaVersion[] = [];
    activityPackTypeName: string;
    activityPackTypeDescription: string;
    pageSize = 4;
    pageEvent: PageEvent;
    currentPageItemLength: number;
    iconUrl: string;
    iconPacks = [];
    isSchemaUpdated: boolean;
    updatedSchema: any;
    currentInstanceName: string;
    currentAssetType: string;
    instanceNameSub: Subscription;
    assetTypeSub: Subscription;
    status: ActivityStatusType[];
    changedColorList: ActivityPackStatus[];
    isVersionAdd: boolean;
    activityPackVersionList: SchemaVersion[];
    displayVersionList: SchemaVersion[];
    activityPackForm: FormGroup;
    isReadOnly: boolean = false;
    constructor(
        private ngbActiveModal: NgbActiveModal,
        private cd: ChangeDetectorRef,
        private instanceDataService: InstanceDataService,
        private formBuilder: FormBuilder,
        private customValidator: CustomValidatorService,
        private customAvatarService: CustomAvatarService
    ) {
        this.editorOptions = new JsonEditorOptions();
        this.editorOptions.modes = ['code', 'text', 'tree', 'view']; // set all allowed modes

        this.activityPackTypeName = '';
        this.activityPackVersionList = [];
        this.displayVersionList = [];
        this.isVersionAdd = false;
    }

    ngOnInit(): void {
        this.iconUrl = this.activityPackType?.icon;

        this.activityPackForm = this.formBuilder.group({
            name: new FormControl(this.activityPackType?.name || '', [Validators.required, UniqueNameValidation(this.activityPackNameList)]),
            iconUrl: new FormControl(this.activityPackType?.icon, []),
            description: new FormControl(this.activityPackType?.description, Validators.required),
            tagStandard: new FormControl(this.activityPackType?.tagStandardId), //, Validators.required
        });
        if (this.activityPackType?.name.toLowerCase() == 'exception' || this.activityPackType?.name.toLowerCase() == 'operational leak') {
            this.isReadOnly = true;
        } else {
            this.isReadOnly = false;
        }
        if (this.activityPackType?.schemaVersions) {
            this.activityPackType.schemaVersions.forEach((version) => {
                this.activityPackVersionList.push({
                    schemaVersionId: version.schemaVersionId,
                    version: version.version,
                    schema: JSON.parse(version.schema),
                });
            });
        }

        this.currentPageItemLength = this.activityPackVersionList.length;
        this.displayVersionList = this.activityPackVersionList.slice().reverse().slice(0, 4);

        this.instanceNameSub = this.instanceDataService.currentInstanceName.subscribe((instanceName: string) => {
            this.currentInstanceName = instanceName;
        });
        this.assetTypeSub = this.instanceDataService.currentAssetType.subscribe((assetType: string) => {
            this.currentAssetType = assetType;
        });
    }

    closeModalWithCancel() {
        this.ngbActiveModal.close(false);
        this.reverseSchemaList = this.schemaList;
    }

    onIconPickerSelect(item: ActivityIcon) {
        this.iconUrl = item.path;
        this.activityPackForm.get('iconUrl').setValue(item.path);
        this.activityPackForm.markAsDirty();
    }

    // * Save button click
    saveActivityPackType() {
        if (this.modalMode == 'edit') {
            this.activityPackType.name = this.activityPackForm.value.name;
            this.activityPackType.description = this.activityPackForm.value.description;
            this.activityPackType.icon = this.activityPackForm.value.iconUrl;
            this.activityPackType.tagStandardId = this.activityPackForm.value.tagStandard;
            if (this.isVersionAdd) {
                const schemaRevision = new SchemaRevision();
                schemaRevision.schema = typeof this.updatedSchema == 'object' ? JSON.stringify(this.updatedSchema) : this.updatedSchema;
                this.activityPackType.schemaRevision = schemaRevision;
            }
            this.ngbActiveModal.close(this.activityPackType);
        } else if (this.modalMode == 'add') {
            const newActivityPack = new ActivityPackType();
            newActivityPack.name = this.activityPackForm.value.name;
            newActivityPack.description = this.activityPackForm.value.description;
            newActivityPack.icon = this.activityPackForm.value.iconUrl;
            newActivityPack.tagStandardId = this.activityPackForm.value.tagStandard;
            newActivityPack.isSelected = false;
            if (this.isVersionAdd) {
                const schemaRevision = new SchemaRevision();
                schemaRevision.schema = typeof this.updatedSchema == 'object' ? JSON.stringify(this.updatedSchema) : this.updatedSchema;
                newActivityPack.schemaRevision = schemaRevision;
            }
            this.ngbActiveModal.close(newActivityPack);
        }
    }

    // * method to add new version
    addNewVersion(): void {
        this.isVersionAdd = true;
        let emptyObj = {};
        this.activityPackForm.markAsDirty();
        if (this.modalMode == 'edit') {
            // * while editing the activity pack type
            const version = new SchemaVersion();
            if (this.activityPackType?.schemaVersions.length > 0) {
                version.schema = JSON.parse(this.activityPackType.schemaVersions[this.activityPackType.schemaVersions.length - 1].schema);
            } else {
                version.schema = JSON.parse(JSON.stringify(emptyObj));
            }
            version.version = this.activityPackVersionList.length + 1;
            this.activityPackVersionList = [...this.activityPackVersionList, version];
            this.updatedSchema = version.schema;
        } else if (this.modalMode == 'add') {
            // * while creating the activity pack type
            const version = new SchemaVersion();
            version.schema = JSON.parse(JSON.stringify(emptyObj));
            version.version = this.activityPackVersionList.length + 1;
            this.activityPackVersionList = [...this.activityPackVersionList, version];
            this.updatedSchema = version.schema;
        }

        if (this.activityPackVersionList.length > 4) {
            this.currentPageItemLength = this.activityPackVersionList.length;
            this.cd.detectChanges();
            this.paginator.firstPage();
        }

        this.displayVersionList = this.activityPackVersionList.slice().reverse().slice(0, 4);
    }

    // * helper method to get the schema data
    getSchemaData(schemaVersionId: string) {
        if (schemaVersionId) {
            let schema = this.activityPackVersionList.find((schema) => schema.schemaVersionId == schemaVersionId);
            return schema.schema;
        } else {
            return this.activityPackVersionList[this.activityPackVersionList.length - 1].schema;
        }
    }

    // * helper method to return the custom json-editor options
    getJsonEditorOptions(schemaVersion: SchemaVersion) {
        this.editorOptions = new JsonEditorOptions();
        this.editorOptions.modes = ['tree', 'view', 'code', 'text'];

        // ✅ Enable necessary UI components
        this.editorOptions.mainMenuBar = true; // Enables the menu bar
        this.editorOptions.navigationBar = true; // Enables the navigation bar
        this.editorOptions.statusBar = true; // Enables the status bar
        if (schemaVersion.schemaVersionId) {
            // * return json options with validation not to edit the the schema
            this.editorOptions.onEditable = () => {
                return false;
            };
        } else {
            // * return json options with  allow to edit the the schema
            this.editorOptions.onEditable = () => {
                return true;
            };

            // * getting the json schema
            this.editorOptions.onChangeText = (event) => {
                this.updatedSchema = event;
                try {
                    const parsedSchema = JSON.parse(event);
                    if (!this.editor.isValidJson() || Object.keys(parsedSchema).length === 0) {
                        this.activityPackForm.setErrors({
                            inValidSchema: 'Schema is not valid',
                        });
                    } else {
                        this.activityPackForm.setErrors(null);
                    }
                } catch (error) {
                    this.activityPackForm.setErrors({
                        inValidSchema: 'Schema is not valid',
                    });
                }
            };
        }
        return this.editorOptions;
    }

    // * pagination handler
    getPaginationData(event: PageEvent) {
        this.displayVersionList = this.activityPackVersionList
            .slice()
            .reverse()
            .slice(event.pageIndex * event.pageSize, event.pageIndex * event.pageSize + event.pageSize);
        return event;
    }

    checkDisable() {
        if (this.modalMode == 'add') {
            return !(this.activityPackForm.valid && this.activityPackForm.dirty && this.isSchemaValid());
        } else {
            return !(this.activityPackForm.valid && this.activityPackForm.dirty);
        }
    }
    isSchemaValid(): boolean {
        try {
            const parsedSchema = JSON.parse(this.updatedSchema);

            if ((!this.updatedSchema && !this.editor.isValidJson()) || Object.keys(parsedSchema).length === 0) {
                return false;
            } else {
                return true;
            }
        } catch (error) {
            return false;
        }
    }

    onKeyPress(event: KeyboardEvent) {
        this.customValidator.validateKeyPress(event);
    }

    getInitials(name: string): string {
        if (!name) return '';
        return this.customAvatarService.getShortInitials(name);
    }
}
